import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Building2, MapPin, Plus } from 'lucide-react';
import { fetchTenantBilling, fetchTenantLocations, type TenantLocationRow } from '@/lib/api';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { Modal } from '@/components/ui/Modal';
import { ResponsiveTable, type Column } from '@/components/ui/ResponsiveTable';
import { TenantAddLocationForm } from '@/components/ui/TenantAddLocationForm';
import { TenantBilling } from './TenantBilling';

/**
 * Super-admin tenant detail. A back link, a header with the tenant name and
 * subscription email, the shared TenantBilling panel scoped to this tenant,
 * and a Locations card that lists the tenant's locations with an
 * add-location control (super_admin CMS provisioning).
 *
 * The header reads the SAME ['tenant-billing', id] query the TenantBilling
 * panel uses, so react-query dedupes them into one request.
 *
 * super_admin only by convention: the nav leaf and the GET routes gate to
 * super_admin. A non-super_admin who hand-types the URL gets the panel's
 * error state from the 403, no data leaks. The locations list and the
 * create routes are gated server-side by requireTenantScope the same way.
 */

const TYPE_LABELS: Record<string, string> = {
  residential: 'Residential',
  multifamily: 'Multi-Family',
  commercial: 'Commercial',
  public: 'Public',
  fleet: 'Fleet',
};

// Card chrome mirrors TenantBilling so the Locations card reads as a sibling.
const cardStyle: React.CSSProperties = {
  padding: '16px 18px', background: 'var(--color-sf2, #1a1a1a)',
  border: '1px solid var(--color-brd, var(--evi-border))', borderRadius: 10,
};
const accentBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '8px 14px', fontSize: 13,
  background: 'var(--evi-accent, #2ECC71)', color: '#000',
  border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer',
};

const LOCATION_COLUMNS: Column<TenantLocationRow>[] = [
  {
    key: 'name', header: 'Name', mobile: 'primary',
    cell: (loc) => (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <MapPin size={13} style={{ color: 'var(--color-tx-m)' }} />
        {loc.name}
      </span>
    ),
  },
  {
    key: 'type', header: 'Type', mobile: 'body',
    cell: (loc) => TYPE_LABELS[loc.type] ?? loc.type,
  },
  {
    key: 'chargers', header: 'Chargers', mobile: 'body', align: 'right',
    cell: (loc) => loc.charger_count,
  },
  {
    key: 'status', header: 'Status', mobile: 'body',
    cell: (loc) => (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span style={{
          width: 7, height: 7, borderRadius: '50%', display: 'inline-block',
          background: loc.is_active ? 'var(--evi-accent, #2ECC71)' : 'var(--color-tx-m)',
        }} />
        {loc.is_active ? 'Active' : 'Inactive'}
      </span>
    ),
  },
];
export function TenantDetailPage({
  tenantId, onNavigate,
}: {
  tenantId: string;
  onNavigate: (path: string) => void;
}) {
  const billingQ = useQuery({
    queryKey: ['tenant-billing', tenantId],
    queryFn: () => fetchTenantBilling(tenantId),
  });
  useRecentRoute(billingQ.data?.name ?? 'Operator detail');

  const locationsQ = useQuery({
    queryKey: ['tenant-locations', tenantId],
    queryFn: () => fetchTenantLocations(tenantId),
  });
  const [addOpen, setAddOpen] = useState(false);

  return (
    <div style={{ padding: '24px 28px' }}>
      <button
        type="button"
        onClick={() => onNavigate('/tenants')}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '6px 0', background: 'transparent', border: 'none',
          color: 'var(--color-tx-m)', cursor: 'pointer', fontSize: 12, marginBottom: 12,
        }}
      >
        <ArrowLeft size={14} /> Back to operators
      </button>

      <header style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 18 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10,
          background: 'color-mix(in srgb, var(--evi-accent, #2ECC71) 12%, transparent)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--evi-accent, #2ECC71)',
        }}>
          <Building2 size={18} />
        </div>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>{billingQ.data?.name ?? 'Operator'}</h1>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--color-tx-m)' }}>
            {billingQ.data?.billing_email ?? 'Subscription billing'}
          </p>
        </div>
      </header>

      <TenantBilling tenantId={tenantId} />

      <section style={{ ...cardStyle, marginTop: 18 }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, marginBottom: 14,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <MapPin size={16} style={{ color: 'var(--evi-accent, #2ECC71)' }} />
            <h2 style={{ margin: 0, fontSize: 18 }}>Locations</h2>
            {locationsQ.data && (
              <span style={{ fontSize: 12, color: 'var(--color-tx-m)' }}>
                {locationsQ.data.length} location{locationsQ.data.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
          <button type="button" onClick={() => setAddOpen(true)} style={accentBtnStyle}>
            <Plus size={14} /> Add location
          </button>
        </div>

        {locationsQ.isLoading ? (
          <p style={{ margin: 0, fontSize: 13, color: 'var(--color-tx-m)' }}>Loading locations…</p>
        ) : locationsQ.isError ? (
          <p style={{ margin: 0, fontSize: 13, color: 'var(--evi-error, #ef4444)' }}>
            Could not load locations.
          </p>
        ) : (
          <ResponsiveTable
            columns={LOCATION_COLUMNS}
            rows={locationsQ.data ?? []}
            rowKey={(loc) => loc._id}
            onRowClick={(loc) => onNavigate(`/locations/${loc._id}`)}
            emptyMessage="No locations yet. Add one to start provisioning chargers."
          />
        )}
      </section>

      <Modal isOpen={addOpen} onClose={() => setAddOpen(false)} title="Add location">
        <TenantAddLocationForm
          tenantId={tenantId}
          onSuccess={() => { setAddOpen(false); void locationsQ.refetch(); }}
          onCancel={() => setAddOpen(false)}
        />
      </Modal>
    </div>
  );
}
